'use client';

import { ParsedUrlQuery } from 'querystring';

import { Identify, Types } from '@amplitude/analytics-browser';
import { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { Experiment, ExperimentClient, Variant } from '@amplitude/experiment-js-client';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useBreakPoint } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { User } from '../../../resources/user/domain/types/User';
import { usePage } from '../../../shared/providers/page/usePage';
import { isBot } from '../../../shared/utilities/browser/isBot';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';
import { logger } from '../../Log/logger';

import { Ampli, ampli, ApiKey, Environment } from './__AUTO_GENERATED__';

export type TrackingProperties<F extends (properties: Parameters<F>[0]) => void> = Omit<
    Parameters<F>[0],
    keyof ReturnType<typeof getDefaultTrackingProperties>
>;

type AssignationData = Record<string, string>;

function getAmplitudeApiKey(): string | null {
    const env = process.env.NEXT_PUBLIC_AMPLITUDE_ENV;

    if (!env) {
        return null;
    }

    return ApiKey[env as Environment];
}

function getExperimentDeployKey(): string {
    const deployKey = process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOY_KEY;

    if (!deployKey) {
        throw new Error('Amplitude Experiment missing a deploy key');
    }

    return deployKey;
}

class SkipUserPropertiesSyncEnrichmentPlugin implements EnrichmentPlugin {
    name = 'skip-user-properties-sync-enrichment-plugin';
    type = 'enrichment' as const;

    async execute(event: Event): Promise<Event> {
        if (event.event_type === 'session_start') {
            // @ts-expect-error - Property '$skip_user_properties_sync' is not included in the Event type
            event.$skip_user_properties_sync = true;
        }
        return event;
    }
}

type AmplitudeContextValue = {
    setIsConnected: (user: UserForAmplitude) => void;
    isAmplitudeEnabled: boolean;
    experimentClient: ExperimentClient | null;
    client: Types.BrowserClient | null;
};

const AmplitudeContext = React.createContext<AmplitudeContextValue>({
    setIsConnected: () => {
        return;
    },
    isAmplitudeEnabled: false,
    experimentClient: null,
    client: null,
});

export type UserForAmplitude = PickDomain<
    User,
    'accountId' | 'isEvaneos' | 'recommendationsIsEnabled' | 'numberOfUserQuotesByStateCounters',
    {
        status: User['status'] | null;
    }
>;

const identifiedUser = new Identify();
let isAmplitudeAlreadyLoaded = false;

export const AmplitudeProvider = (
    props: PropsWithChildren<{
        isUserConnectedOnLoad: boolean;
    }>,
) => {
    const [hasConsented, setHasConsented] = useState(false);
    const [isAmplitudeLoaded, setIsAmplitudeLoaded] = useState(isAmplitudeAlreadyLoaded);
    const isAmplitudeClientCreated = Boolean(ampli.isLoaded);
    const isAmplitudeEnabled = hasConsented && isAmplitudeLoaded;
    const experimentClient = useRef<ExperimentClient | null>(null);
    const { isMobile } = useBreakPoint();
    const amplitudeApiKey = getAmplitudeApiKey();

    /** Manually set the isConnected user property for instant availability of the information in amplitude tracking */
    const setIsConnected = useCallback(
        (user: UserForAmplitude) => {
            if (!isAmplitudeEnabled) {
                return;
            }

            ampli.client.setUserId(user.accountId);

            identifiedUser.set('isConnected', true);
            identifiedUser.set('is_evaneos', user.isEvaneos);
            identifiedUser.set('ab_reco_available', user.recommendationsIsEnabled);
            if (user.status) {
                identifiedUser.set('user_status', user.status);
            }

            if (user.numberOfUserQuotesByStateCounters) {
                identifiedUser.set('nb_request_done', user.numberOfUserQuotesByStateCounters.done);
                identifiedUser.set(
                    'nb_request_confirmed',
                    user.numberOfUserQuotesByStateCounters.confirmed,
                );
                identifiedUser.set(
                    'nb_request_cancelled',
                    user.numberOfUserQuotesByStateCounters.cancelled,
                );
                identifiedUser.set(
                    'nb_request_in_progress',
                    user.numberOfUserQuotesByStateCounters.in_progress,
                );
                identifiedUser.set(
                    'nb_request_planning',
                    user.numberOfUserQuotesByStateCounters.planning,
                );
            }

            ampli.client.identify(identifiedUser);
        },
        [isAmplitudeEnabled],
    );

    const contextProviderValue = useMemo(() => {
        return {
            setIsConnected,
            isAmplitudeEnabled,
            client: isAmplitudeLoaded ? ampli.client : null,
            experimentClient: experimentClient.current,
        };
    }, [isAmplitudeEnabled, isAmplitudeLoaded, setIsConnected]);

    if (amplitudeApiKey && !isAmplitudeClientCreated && !isAmplitudeLoaded && hasConsented) {
        identifiedUser.set('device_category', isMobile() ? 'mobile' : 'desktop');
        identifiedUser.set('isConnected', props.isUserConnectedOnLoad);

        ampli
            .load({
                client: {
                    apiKey: amplitudeApiKey,
                    configuration: {
                        serverZone: 'EU',
                        autocapture: {
                            pageViews: false,
                            sessions: true,
                            attribution: true,
                            fileDownloads: false,
                            formInteractions: false,
                        },
                    },
                },
            })
            .promise.then(() => {
                return ampli.client.add(new SkipUserPropertiesSyncEnrichmentPlugin()).promise;
            })
            .then(() => {
                return ampli.client.add(
                    sessionReplayPlugin({ sampleRate: sessionReplaySampleRate() }),
                ).promise;
            })
            .then(() => {
                return ampli.client.identify(identifiedUser);
            })
            .then(() => {
                experimentClient.current = Experiment.initializeWithAmplitudeAnalytics(
                    getExperimentDeployKey(),
                    {
                        serverUrl: 'https://api.lab.eu.amplitude.com',
                    },
                );
                window.amplitude = ampli.client;
                isAmplitudeAlreadyLoaded = true;
                setIsAmplitudeLoaded(true);
                logger.info('Amplitude loaded', {
                    experimentClient: Boolean(experimentClient.current),
                });
            })
            .catch((error) => {
                logger.error('Amplitude load failed', {}, error);
            });
    }

    useEffect(function listenTrackingConsent() {
        function onConsentChanged() {
            setHasConsented(Boolean(window.Didomi?.getUserConsentStatusForVendor('c:amplitude')));
        }
        window.didomiEventListeners = window.didomiEventListeners || [];
        window.didomiEventListeners.push({
            event: 'consent.changed',
            listener: onConsentChanged,
        });
        window.didomiOnReady = window.didomiOnReady || [];
        window.didomiOnReady.push(onConsentChanged);
    }, []);

    return (
        <AmplitudeContext.Provider value={contextProviderValue}>
            {props.children}
        </AmplitudeContext.Provider>
    );
};

function sessionReplaySampleRate(): number {
    const sampleRate = process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? '0';
    return parseFloat(sampleRate);
}

function getCanonicalUrl() {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
        return canonical.getAttribute('href');
    }

    return null;
}

function removeIgnoredQueryParams(url: URL) {
    const QUERY_PARAMS_TO_IGNORE = ['jwt', 'email'];
    QUERY_PARAMS_TO_IGNORE.forEach((element) => {
        url.searchParams.delete(element);
    });
    return url;
}

function getPathnameFromUrl(urlToParse: string) {
    const url = document.createElement('a');
    url.href = urlToParse;

    return url.pathname;
}

function getReferrer() {
    return document.referrer || '$direct';
}

function getReferringDomain(referrer: string) {
    const split = referrer.split('/');
    if (split.length >= 3) {
        return split[2];
    }

    return '';
}

function getUrl(search: string) {
    const canonicalUrl = getCanonicalUrl();
    const url = canonicalUrl
        ? canonicalUrl.indexOf('?') > -1
            ? canonicalUrl
            : canonicalUrl + search
        : window.location.href;
    const hashIndex = url.indexOf('#');

    return hashIndex > -1 ? url.slice(0, hashIndex) : url;
}

function getHostname() {
    return window.location.hostname;
}

function getDefaultTrackingProperties(seoContext: string) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return {
            category: '',
            path: '',
            referrer: '',
            search: '',
            title: '',
            url: '',
            referring_domain: '',
            tab_url: '',
            site_name: '',
        };
    }

    const canonicalUrl = getCanonicalUrl();
    const sanitizedLocation = removeIgnoredQueryParams(new URL(window.location.href));
    const path = canonicalUrl ? getPathnameFromUrl(canonicalUrl) : sanitizedLocation.pathname;
    const { search } = sanitizedLocation;
    const { title } = document;
    const url = getUrl(search);
    const tabUrl = sanitizedLocation.href;
    const referrer = getReferrer();
    const referringDomain = getReferringDomain(referrer);
    const siteName = getHostname();

    return {
        category: seoContext,
        path,
        referrer,
        search,
        title,
        url,
        referring_domain: referringDomain,
        tab_url: tabUrl,
        site_name: siteName,
    };
}

export const useAmplitude = () => {
    return useContext(AmplitudeContext);
};

/**
 * Custom hook to fetch and return the variant of an experiment using Amplitude.
 *
 * @param experimentName - The name of the experiment to fetch the variant for.
 * @param assignationData - Optional data used for experiment assignations. `site_name` and `page` are automatically injected.
 * @returns The variant of the experiment.
 *
 * This hook utilizes the Amplitude SDK to fetch the variant of a given experiment.
 * It first checks if Amplitude is enabled and if the experiment object is available.
 * It then fetches the experiment with the user properties merged with the assignation data.
 * Once the fetch is complete, it sets the variant of the experiment.
 *
 * @example
 * const experiment = useExperimentVariation('my-experiment', { destination_id: '28' });
 * const variant = experiment?.value;
 */
/**
 * Get query params from URL (works in both App Router and Pages Router)
 * Only extracts experiment-related params for debug/testing
 */
export function getExperimentQueryParams(): ParsedUrlQuery {
    if (typeof window === 'undefined') {
        return {};
    }

    const searchParams = new URLSearchParams(window.location.search);
    const query: ParsedUrlQuery = {};

    const withExperimentName = searchParams.get('withExperimentName');
    const withExperimentVariation = searchParams.get('withExperimentVariation');

    if (withExperimentName) {
        query.withExperimentName = withExperimentName;
    }
    if (withExperimentVariation) {
        query.withExperimentVariation = withExperimentVariation;
    }

    return query;
}

export const useLazyExperimentVariation = (
    experimentName: string,
    assignationData?: AssignationData,
): (() => Promise<Variant | undefined>) => {
    const { isAmplitudeEnabled, experimentClient } = useAmplitude();
    const { seoContext } = usePage();

    const variantRef = useRef<Variant | undefined>(undefined);
    const fetchPromiseRef = useRef<Promise<Variant | undefined> | null>(null);

    const getOrFetchVariant = useCallback(() => {
        if (variantRef.current) {
            return Promise.resolve(variantRef.current);
        }
        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current;
        }
        const promise = fetchExperimentVariation(
            isAmplitudeEnabled,
            experimentClient,
            seoContext,
            experimentName,
            assignationData,
            getExperimentQueryParams(),
        ).then((variant) => {
            variantRef.current = variant;
            return variant;
        });
        fetchPromiseRef.current = promise;
        return promise;
    }, [isAmplitudeEnabled, experimentClient, seoContext, experimentName, assignationData]);

    return getOrFetchVariant;
};

export const useExperimentVariation = (
    experimentName: string,
    assignationData?: AssignationData,
) => {
    const { isAmplitudeEnabled, experimentClient } = useAmplitude();
    const { seoContext } = usePage();
    const [variant, setVariant] = useState<Variant>();
    const refAssignationData = useRef(assignationData);

    useEffect(() => {
        const query = getExperimentQueryParams();

        void fetchExperimentVariation(
            isAmplitudeEnabled,
            experimentClient,
            seoContext,
            experimentName,
            refAssignationData.current,
            query,
        ).then((variant) => {
            if (variant) {
                setVariant(variant);
            }
        });
    }, [isAmplitudeEnabled, experimentClient, experimentName, seoContext]);

    return variant;
};

function shouldDisableExperiments(): boolean {
    return isBot();
}

export function fetchExperimentVariation(
    isAmplitudeEnabled: boolean,
    experiment: ExperimentClient | null,
    seoContext: string,
    experimentName: string,
    assignationData?: AssignationData,
    query?: ParsedUrlQuery,
): Promise<Variant | undefined> {
    if (shouldDisableExperiments()) {
        return Promise.resolve(undefined);
    }

    const hasExperimentQueryparam =
        query && query.withExperimentName === experimentName && query.withExperimentVariation;

    if (!hasExperimentQueryparam && isAmplitudeEnabled && experiment) {
        const userProperties = identifiedUser.getUserProperties().$set || {};
        const experimentsProperties = {
            page: seoContext,
            site_name: getHostname(),
        };

        return experiment
            .fetch(
                {
                    user_properties: {
                        ...userProperties,
                        ...experimentsProperties,
                        ...assignationData,
                    },
                },
                {
                    flagKeys: [experimentName],
                },
            )
            .then(() => {
                return experiment.variant(experimentName);
            });
    }
    if (hasExperimentQueryparam) {
        // Example: ?withExperimentName=<myExperimentName>&withExperimentVariation=<myExperimentVariation>
        return Promise.resolve({ value: query.withExperimentVariation as string });
    }
    return Promise.resolve(undefined);
}

export const useTrackPageViewAmplitude = (
    callback: (
        ampli: Ampli,
        defaultProperties: ReturnType<typeof getDefaultTrackingProperties>,
    ) => void,
) => {
    const { isAmplitudeEnabled } = useAmplitude();
    const callbackRef = useRef(callback);
    const { seoContext } = usePage();

    useEffect(() => {
        if (isAmplitudeEnabled) {
            callbackRef.current(ampli, getDefaultTrackingProperties(seoContext));
        }
    }, [isAmplitudeEnabled, seoContext]);
};

export type TrackEventType = (
    callback: (
        ampli: Ampli,
        defaultProperties: ReturnType<typeof getDefaultTrackingProperties>,
    ) => void,
) => void;

export const useTrackEventAmplitude = () => {
    const { isAmplitudeEnabled } = useAmplitude();
    const { seoContext } = usePage();

    const trackEvent: TrackEventType = useCallback(
        (callback) => {
            if (isAmplitudeEnabled) {
                callback(ampli, getDefaultTrackingProperties(seoContext));
            }
        },
        [isAmplitudeEnabled, seoContext],
    );

    return {
        trackEvent,
    };
};
